// layouts/Dashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppShell, useShell } from '@scaletimepulse/pulse-ui';
import { DemoProvider, useDemo, DEMO_BID_TEMPLATE } from '../components/demo/DemoContext';
import BidTemplateDemo from '../components/demo/BidTemplateDemo';
import BookCallPrompt from '../components/layout/BookCallPrompt';
import { setQbConnected } from '../store/actions/user';
import { logout } from '../store/actions';

// ── PulseDemo pages ───────────────────────────────────────────────────────────
import FinanceDashboard from '../pages/Finance/Finance';
import ClientServicingDashboard from '../pages/ClientServicing/ClientServicing';
import TeamServicing from '../pages/TeamServicing/TeamServicing';
import PLScenarioPlayground from '../pages/Playground/Playground';
import PipelineDashboard from '../pages/DataPipeline/Pipeline';
import MarketingDashboard from '../pages/Marketing';
import ConfigurationPage from '../pages/Settings/Configurations';
import Architecture from '../pages/Architecture/Architecture';

// ── Bid Template ─────────────────────────────────────────────────────────────
import BidTemplatePage from '../pages/BidTemplate/BidTemplatePage';

// ── ScaleTime demo modules ────────────────────────────────────────────────────
import WebinarFunnel from '../pages/WebinarFunnel/WebinarFunnel';
import ColdCalendar from '../pages/ColdCalendar/ColdCalendar';
import SalesFunnel from '../pages/SalesDashboard/SalesDashboard';

// ── Training modules (sidebar-grouped under Knowledge) ───────────────────────
import { TrainingProvider } from '../pages/Training/TrainingContext';
import Assignments from '../pages/Training/Assignments';
import Modules from '../pages/Training/Modules';

// ── Knowledge: Wiki + Org Chart ───────────────────────────────────────────────
import Wiki from '../pages/Knowledge/Wiki';
import OrgChart from '../pages/Knowledge/OrgChart';

// ── Icons ─────────────────────────────────────────────────────────────────────
import {
  BanknotesIcon,
  UserGroupIcon,
  UsersIcon,
  LightBulbIcon,
  DocumentTextIcon,
  MegaphoneIcon,
  ChartBarIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  CircleStackIcon,
  Cog6ToothIcon,
  ClipboardDocumentListIcon,
  BookOpenIcon,
  RectangleGroupIcon,
  AcademicCapIcon,
  ShareIcon,
  ArrowRightOnRectangleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';

const BASE_PATH = '/dashboard';

// AppShell tracks the active page in its own state (module ids), not the URL. Module ids are
// the route paths minus the leading slash, so `/dashboard/<path>` links keep working.
const moduleIdForPath = (path) => path.replace(/^\//, '');
const normalizePath = (pathname) => pathname.toLowerCase().replace(/\/+$/, '');

// Rendered around the active page: keeps the URL and AppShell's active module in step, in
// both directions — picking a page in the sidebar/menus updates the URL, and a URL change
// from elsewhere (navigate(), back/forward) switches the active page.
function RoutedPage({ id, path, resolveId, fallbackId, children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { setActive } = useShell();
  const seenPath = useRef(pathname);
  const claimedUrl = useRef(false);

  useEffect(() => {
    const urlId = resolveId(pathname);

    if (pathname === seenPath.current) {
      // just mounted via the shell: bring the URL in line (replace when the URL was unknown)
      if (urlId !== id && !claimedUrl.current) {
        claimedUrl.current = true;
        navigate(path, { replace: !urlId });
      }
      return;
    }

    seenPath.current = pathname;
    const targetId = urlId || fallbackId;
    if (targetId !== id) setActive(targetId);
    else if (!urlId) navigate(path, { replace: true });
  }, [pathname, id, path, resolveId, fallbackId, navigate, setActive]);

  return children;
}

function DashboardShell() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { start: startDemo } = useDemo();
  const user = useSelector((state) => state.user);

  // ── Training assignment cross-page state ───────────────────────────────────
  // Kept here so SOP Library can trigger the Assignments modal cross-route
  const [showAssignModal, setShowAssignModal]   = useState(false);
  const [preselectedSopId, setPreselectedSopId] = useState(null);

  const dispatch    = useDispatch();
  const userRole    = useSelector((state) => state.user.role);
  const userEmail   = useSelector((state) => state.user.email);
  const isGuestState = useSelector((state) => state.user.isGuest);
  const isAdmin     = userRole === 'admin';
  const isGuest     = Boolean(isGuestState)
    || (userEmail || '').toLowerCase() === 'guest@scaletime.co'
    || (typeof window !== 'undefined' && window.localStorage?.getItem('is_guest') === 'true');

  useEffect(() => {
    const handleStorage = (event) => {
      if (event.key === 'qb_connected' && event.newValue === 'true') {
        dispatch(setQbConnected(true));
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [dispatch]);

  const routes = [

    // ── Pulse BI ───────────────────────────────────────────────────────────
    {
      title: 'Pulse BI',
      pages: [
        ...(isAdmin ? [{
          name: 'Finance',
          path: '/finance',
          icon: BanknotesIcon,
          element: <FinanceDashboard />,
        }] : []),
        {
          name: 'Sales',
          path: '/sales_funnel',
          icon: CurrencyDollarIcon,
          element: <SalesFunnel />,
        },
        {
          name: 'Client',
          path: '/client',
          icon: UserGroupIcon,
          element: <ClientServicingDashboard />,
        },
        {
          name: 'Team',
          path: '/team',
          icon: UsersIcon,
          element: <TeamServicing />,
        },
        {
          name: 'Marketing',
          path: '/marketing',
          icon: MegaphoneIcon,
          element: <MarketingDashboard />,
          children: [
            {
              name: 'Webinar Funnel',
              path: '/webinar_funnel',
              icon: ChartBarIcon,
              element: <WebinarFunnel />,
            },
            {
              name: 'Cold Calendar',
              path: '/cold_calendar',
              icon: CalendarDaysIcon,
              element: <ColdCalendar />,
            },
          ],
        },
      ],
    },

    // ── Oper. Intelligence ─────────────────────────────────────────────────
    ...(isAdmin ? [{
      title: 'Ops Intelligence',
      pages: [
        {
          name: 'P&L Playground',
          path: '/playground',
          icon: LightBulbIcon,
          element: <PLScenarioPlayground />,
        },
        {
          name: 'Bid Template',
          path: '/bid_template',
          icon: DocumentTextIcon,
          element: <BidTemplatePage />,
        },
      ],
    }] : []),

    // ── Knowledge Hub ──────────────────────────────────────────────────────
    {
      title: 'Knowledge Hub',
      pages: [
        {
          name: 'Wiki',
          path: '/knowledge/wiki',
          icon: BookOpenIcon,
          element: <Wiki />,
        },
        {
          name: 'Org Chart',
          path: '/knowledge/org-chart',
          icon: ShareIcon,
          element: <OrgChart />,
        },
        {
          name: 'Onboarding',
          path: '/training/assignments',
          icon: ClipboardDocumentListIcon,
          element: (
            <Assignments
              showAssignModal={showAssignModal}
              setShowAssignModal={setShowAssignModal}
              preselectedSopId={preselectedSopId}
              setPreselectedSopId={setPreselectedSopId}
            />
          ),
        },
        {
          name: 'Training',
          path: '/training/modules',
          icon: AcademicCapIcon,
          element: <Modules />,
        },
      ],
    },

    // ── Settings (hidden from sidebar — surfaced via SettingsPanel) ────────
    {
      title: 'Settings',
      hiddenFromSidebar: true,
      pages: [
        ...(isAdmin ? [{
          name: 'Configurations',
          path: '/configurations',
          icon: Cog6ToothIcon,
          element: <ConfigurationPage />,
        }] : []),
        ...(isGuest ? [] : [{
          name: 'Data Sync',
          path: '/data_sync',
          icon: CircleStackIcon,
          element: <PipelineDashboard />,
        }]),
        ...(isAdmin && !isGuest ? [{
          name: 'Architecture',
          path: '/architecture',
          icon: RectangleGroupIcon,
          element: <Architecture />,
        }] : []),
      ],
    },
  ];

  // ── routes → AppShell modules ──────────────────────────────────────────────
  const fallbackId = isAdmin ? 'finance' : 'client';
  const allPages = routes.flatMap(({ pages }) =>
    pages.flatMap((page) => [page, ...(page.children || [])])
  );
  const resolveId = (path) => {
    const match = allPages.find(
      (page) => normalizePath(`${BASE_PATH}${page.path}`) === normalizePath(path)
    );
    return match ? moduleIdForPath(match.path) : null;
  };

  const toModule = ({ name, path, icon, element }, extra) => {
    const id = moduleIdForPath(path);
    return {
      id,
      label: name,
      icon,
      ...extra,
      render: () => (
        <RoutedPage
          key={id}
          id={id}
          path={`${BASE_PATH}${path}`}
          resolveId={resolveId}
          fallbackId={fallbackId}
        >
          {element}
        </RoutedPage>
      ),
    };
  };

  // Marketing's sub-pages (Webinar Funnel, Cold Calendar) stay reachable by URL but aren't
  // listed in the sidebar, so they're hidden modules rather than nested nav items.
  const modules = routes.flatMap(({ title, pages, hiddenFromSidebar }) => {
    const placement = hiddenFromSidebar ? { hidden: true } : { section: title };
    return pages.flatMap((page) => [
      toModule(page, placement),
      ...(page.children || []).map((child) => toModule(child, { hidden: true })),
    ]);
  });

  // ── Account + workspace menus ──────────────────────────────────────────────
  const handleBidDemo = () => {
    navigate('/dashboard/bid_template');
    // Give the route transition a tick, then reset the wizard and start the demo.
    setTimeout(() => {
      window.dispatchEvent(new CustomEvent('pulse:reset-bid-wizard'));
      startDemo(DEMO_BID_TEMPLATE);
    }, 50);
  };

  const logoutPressed = () => {
    dispatch(logout());
    navigate('/');
  };

  const userMenu = [
    ...(isAdmin && !isGuest ? [{
      label: 'Architecture',
      icon: <RectangleGroupIcon className="w-5 h-5 text-grey-400" />,
      moduleId: 'architecture',
    }] : []),
    {
      label: 'Bid Template Demo',
      icon: <PlayCircleIcon className="w-5 h-5 text-grey-400" />,
      onClick: handleBidDemo,
    },
    {
      label: 'Log out',
      icon: <ArrowRightOnRectangleIcon className="w-5 h-5" />,
      danger: true,
      onClick: logoutPressed,
    },
  ];

  const settingsMenu = [
    ...(isAdmin ? [{ label: 'Configurations', moduleId: 'configurations' }] : []),
    ...(isGuest ? [] : [{ label: 'Data Sync', moduleId: 'data_sync' }]),
  ];

  return (
    <>
      <AppShell
        ambient={false}
        footerUrl="https://scaletime.co/pulse-dash-lp"
        modules={modules}
        initialModuleId={resolveId(pathname) || fallbackId}
        settingsMenu={settingsMenu}
        user={{
          name: user?.name || 'Guest',
          email: user?.email || undefined,
          avatarUrl: user?.picture || undefined,
        }}
        userMenu={userMenu}
      />

      <BidTemplateDemo />
      <BookCallPrompt />
    </>
  );
}

export function Dashboard() {
  return (
    // TrainingProvider wraps everything so context persists as the user
    // navigates between the three training pages without resetting state
    <TrainingProvider>
      <DemoProvider>
        <DashboardShell />
      </DemoProvider>
    </TrainingProvider>
  );
}

export default Dashboard;
